/**
 * Money API - Bills, budget, and transactions from pasted bank statements
 *
 * Safety: paste-only data entry. No bank connections, ever.
 * Parse is read-only preview; nothing is saved until confirm.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3100';

async function request(path, { method = 'GET', body, token } = {}) {
  const response = await fetch(`${API_BASE}/api/money${path}`, {
    method,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: body !== undefined ? JSON.stringify(body) : undefined
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Request failed (${response.status})`);
  }
  return data;
}

/**
 * Parse pasted statement text. Read-only preview - saves nothing.
 */
export function parseImport(text, token) {
  return request('/import/parse', { method: 'POST', body: { text }, token });
}

/**
 * Save a confirmed batch of transactions and any "Remember this" rules.
 */
export function confirmImport(rows, newRules, token) {
  return request('/import/confirm', { method: 'POST', body: { rows, newRules }, token });
}

/**
 * Undo an entire import batch.
 */
export function undoImport(batchId, token) {
  return request(`/import/${batchId}`, { method: 'DELETE', token });
}

/**
 * List transactions. Filters: month (YYYY-MM), categoryId.
 */
export function getTransactions({ month, categoryId } = {}, token) {
  const params = new URLSearchParams();
  if (month) params.set('month', month);
  if (categoryId) params.set('category_id', categoryId);
  const qs = params.toString();
  return request(`/transactions${qs ? `?${qs}` : ''}`, { token });
}

/**
 * Update category/bill on a transaction.
 */
export function updateTransaction(id, { category_id, bill_id }, token) {
  return request(`/transactions/${id}`, { method: 'PUT', body: { category_id, bill_id }, token });
}

/**
 * Categories
 */
export function getCategories(token) {
  return request('/categories', { token });
}

export function createCategory({ name, type, target_amount, sort_order }, token) {
  return request('/categories', { method: 'POST', body: { name, type, target_amount, sort_order }, token });
}

export function updateCategory(id, updates, token) {
  return request(`/categories/${id}`, { method: 'PUT', body: updates, token });
}

export function deleteCategory(id, token) {
  return request(`/categories/${id}`, { method: 'DELETE', token });
}

/**
 * Rules
 */
export function getRules(token) {
  return request('/rules', { token });
}

export function deleteRule(id, token) {
  return request(`/rules/${id}`, { method: 'DELETE', token });
}

/**
 * Current month as YYYY-MM
 */
export function getCurrentMonth() {
  return new Date().toISOString().slice(0, 7);
}

/**
 * Format an amount in kr with Swedish-style thousands spacing.
 * -1337 -> "-1 337 kr"
 */
export function formatKr(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [intPart, decPart] = abs.toFixed(2).split('.');
  const spaced = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const decimals = decPart === '00' ? '' : `,${decPart}`;
  return `${sign}${spaced}${decimals} kr`;
}
